import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useTheme } from '../context/ThemeContext';
import { useClient } from '../context/ClientContext';
import ClientWorkspaceLayout from '../components/workspace/ClientWorkspaceLayout';
import { Camera, ChevronDown, AlertTriangle, RefreshCw, Utensils } from 'lucide-react';
import { fetchFoodLogs, fetchFoodLogImageObjectUrl, fetchClientDailyTargets } from '../services/clientApi';
import { parseFoodItems, logTotals, logDay, sumTotals } from '../utils/foodLogs';

const CHUNK_SIZE = 100;
const DAYS_PER_PAGE = 7;

const round = (n) => Math.round(Number(n) || 0);
const fmtInt = (n) => round(n).toLocaleString();

// A meal's photo. Fetched with the coach's sign-in only once it scrolls near the screen; falls back to a plate icon.
function MealPhoto({ log, className = '', iconSize = 20 }) {
  const hasImage = Boolean(log.has_image || log.image_url);
  const [src, setSrc] = useState(null);
  const boxRef = useRef(null);

  useEffect(() => {
    if (!hasImage) return undefined;
    let cancelled = false;
    let objectUrl = null;
    const load = async () => {
      try {
        objectUrl = await fetchFoodLogImageObjectUrl(log.id);
        if (cancelled) { URL.revokeObjectURL(objectUrl); objectUrl = null; return; }
        setSrc(objectUrl);
      } catch {
        // The authenticated route can't serve it: a plain public link still works.
        if (!cancelled && log.image_url) setSrc(log.image_url);
      }
    };
    const el = boxRef.current;
    let io = null;
    if (el && 'IntersectionObserver' in window) {
      io = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting) { io.disconnect(); load(); }
      }, { rootMargin: '300px' });
      io.observe(el);
    } else {
      load();
    }
    return () => {
      cancelled = true;
      io?.disconnect();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [log.id, hasImage, log.image_url]);

  return (
    <div ref={boxRef} className={`relative shrink-0 overflow-hidden bg-moss dark:bg-moss-dark ${className}`}>
      {src ? (
        <img src={src} alt="" loading="lazy" className="h-full w-full object-cover" onError={() => setSrc(null)} />
      ) : (
        <span className="flex h-full w-full items-center justify-center text-ink-muted dark:text-ink-muted-dark" aria-hidden="true">
          {hasImage ? <Camera size={iconSize} className="opacity-60" /> : <Utensils size={iconSize} className="opacity-60" />}
        </span>
      )}
    </div>
  );
}

// One meal: a row that opens in place to show its photo, items and scores.
function MealRow({ log, open, onToggle, fmtTime, t }) {
  const items = useMemo(() => parseFoodItems(log), [log]);
  const totals = useMemo(() => logTotals(log), [log]);
  const warnings = Array.isArray(log.dietary_warnings) ? log.dietary_warnings.filter(Boolean) : [];
  const label = log.meal_label || t('Meal', 'ארוחה');
  const summary = items.map((i) => i.name).join(', ');
  const hasScores = log.health_score != null || log.plan_score != null;

  return (
    <li>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="group flex w-full items-center gap-3 px-4 py-3 text-start transition-colors hover:bg-canvas focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-forest dark:hover:bg-canvas-dark"
      >
        <MealPhoto log={log} className="h-14 w-14 rounded-lg" />
        <span className="min-w-0 flex-1">
          <span className="flex items-baseline gap-2">
            <span className="truncate text-sm font-semibold text-ink dark:text-ink-dark">{label}</span>
            <span className="shrink-0 text-[12px] text-ink-muted dark:text-ink-muted-dark">{fmtTime(log.created_at)}</span>
            {warnings.length > 0 && (
              <AlertTriangle size={13} className="shrink-0 text-amber-700 dark:text-amber-400" aria-label={t('Dietary warning', 'אזהרה תזונתית')} />
            )}
          </span>
          <span className="mt-0.5 block truncate text-[13px] text-ink-muted dark:text-ink-muted-dark">
            {summary || t('No items identified', 'לא זוהו פריטים')}
          </span>
        </span>
        <span className="shrink-0 text-end">
          <span className="block text-sm font-semibold tabular-nums text-ink dark:text-ink-dark">{fmtInt(totals.calories)} <span className="text-[11px] font-normal text-ink-muted dark:text-ink-muted-dark">{t('kcal', 'קק״ל')}</span></span>
          <bdi dir="ltr" className="block text-[12px] tabular-nums text-ink-muted dark:text-ink-muted-dark">
            P {round(totals.protein)} · C {round(totals.carbs)} · F {round(totals.fat)}
          </bdi>
        </span>
        <ChevronDown size={16} aria-hidden="true" className={`shrink-0 text-ink-muted transition-transform dark:text-ink-muted-dark ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="space-y-4 bg-canvas/60 px-4 pb-4 pt-1 dark:bg-canvas-dark/40 sm:ps-[4.75rem]">
          {(log.has_image || log.image_url) && (
            <MealPhoto log={log} className="h-56 w-full rounded-lg sm:w-80" iconSize={28} />
          )}

          {items.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[26rem] text-[13px]">
                <thead className="text-[11px] text-ink-muted dark:text-ink-muted-dark">
                  <tr>
                    <th scope="col" className="py-1.5 pe-3 text-start font-medium">{t('Item', 'פריט')}</th>
                    <th scope="col" className="px-2 py-1.5 text-end font-medium">{t('kcal', 'קק״ל')}</th>
                    <th scope="col" className="px-2 py-1.5 text-end font-medium">P</th>
                    <th scope="col" className="px-2 py-1.5 text-end font-medium">C</th>
                    <th scope="col" className="ps-2 py-1.5 text-end font-medium">F</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-line dark:divide-line-dark">
                  {items.map((item, i) => (
                    <tr key={i}>
                      <td className="py-2 pe-3 align-top">
                        <span className="font-medium text-ink dark:text-ink-dark">{item.name}</span>
                        {item.portion && <span className="block text-[12px] text-ink-muted dark:text-ink-muted-dark">{item.portion}</span>}
                        {item.evidence && <span className="mt-0.5 block text-[12px] italic text-ink-muted dark:text-ink-muted-dark">{item.evidence}</span>}
                      </td>
                      <td className="px-2 py-2 text-end tabular-nums text-ink dark:text-ink-dark">{fmtInt(item.calories)}</td>
                      <td className="px-2 py-2 text-end tabular-nums text-ink dark:text-ink-dark">{round(item.protein)}</td>
                      <td className="px-2 py-2 text-end tabular-nums text-ink dark:text-ink-dark">{round(item.carbs)}</td>
                      <td className="ps-2 py-2 text-end tabular-nums text-ink dark:text-ink-dark">{round(item.fat)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-[13px] text-ink-muted dark:text-ink-muted-dark">{t('No item breakdown was saved for this meal.', 'לא נשמר פירוט פריטים לארוחה הזו.')}</p>
          )}

          {warnings.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {warnings.map((w, i) => (
                <span key={i} className="inline-flex items-center gap-1 rounded bg-amber-700/10 px-2 py-0.5 text-[12px] font-medium text-amber-700 dark:bg-amber-400/15 dark:text-amber-400">
                  <AlertTriangle size={12} aria-hidden="true" /> {String(w)}
                </span>
              ))}
            </div>
          )}

          {hasScores && (
            <dl className="grid gap-3 text-[13px] sm:grid-cols-2">
              {log.health_score != null && (
                <div>
                  <dt className="text-[11px] font-medium text-ink-muted dark:text-ink-muted-dark">{t('Health score', 'ציון בריאות')}</dt>
                  <dd className="text-ink dark:text-ink-dark"><span className="font-semibold tabular-nums">{log.health_score}/10</span>{log.health_score_reason && <span className="text-ink-muted dark:text-ink-muted-dark"> · {log.health_score_reason}</span>}</dd>
                </div>
              )}
              {log.plan_score != null && (
                <div>
                  <dt className="text-[11px] font-medium text-ink-muted dark:text-ink-muted-dark">{t('Fits the plan', 'התאמה לתוכנית')}</dt>
                  <dd className="text-ink dark:text-ink-dark"><span className="font-semibold tabular-nums">{log.plan_score}/10</span>{log.plan_score_reason && <span className="text-ink-muted dark:text-ink-muted-dark"> · {log.plan_score_reason}</span>}</dd>
                </div>
              )}
            </dl>
          )}
        </div>
      )}
    </li>
  );
}

export default function ClientFoodLogsPage() {
  const { isDarkMode } = useTheme();
  const { selectedClient } = useClient();

  const [language, setLanguage] = useState(() => localStorage.getItem('language') || 'english');
  const isRTL = language === 'hebrew';
  const t = useCallback((en, he) => (language === 'hebrew' ? he : en), [language]);

  const [logs, setLogs] = useState([]);
  const [status, setStatus] = useState('loading'); // 'loading' | 'ready' | 'error'
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [hasMoreOnServer, setHasMoreOnServer] = useState(true);
  const [visibleDays, setVisibleDays] = useState(DAYS_PER_PAGE);
  const [openId, setOpenId] = useState(null);
  const [targetKcal, setTargetKcal] = useState(0);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    const onLang = () => setLanguage(localStorage.getItem('language') || 'english');
    window.addEventListener('storage', onLang);
    window.addEventListener('languageChange', onLang);
    return () => {
      window.removeEventListener('storage', onLang);
      window.removeEventListener('languageChange', onLang);
    };
  }, []);

  useEffect(() => {
    if (!selectedClient) return undefined;
    let cancelled = false;
    setStatus('loading');
    setLogs([]);
    setOpenId(null);
    setVisibleDays(DAYS_PER_PAGE);
    setHasMoreOnServer(true);
    (async () => {
      const { data, error } = await fetchFoodLogs(selectedClient.user_code, selectedClient.id, null, null, CHUNK_SIZE, 0);
      if (cancelled) return;
      if (error) { setStatus('error'); return; }
      const rows = Array.isArray(data) ? data : (Array.isArray(data?.data) ? data.data : []);
      setLogs(rows);
      setHasMoreOnServer(rows.length >= CHUNK_SIZE);
      setStatus('ready');
    })();
    // The daily calorie target, to show each day against it. Optional: the page works without it.
    fetchClientDailyTargets(selectedClient.id)
      .then((tg) => { if (!cancelled) setTargetKcal(Number(parseFloat(tg?.calories)) || 0); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [selectedClient, reloadKey]);

  // Days, newest first, each with its meals (earliest first) and totals.
  const days = useMemo(() => {
    const byDay = new Map();
    logs.forEach((log) => {
      const day = logDay(log);
      if (!day) return;
      if (!byDay.has(day)) byDay.set(day, []);
      byDay.get(day).push(log);
    });
    return [...byDay.keys()].sort((a, b) => (a < b ? 1 : -1)).map((day) => {
      const meals = byDay.get(day).sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
      return { day, meals, totals: sumTotals(meals.map(logTotals)) };
    });
  }, [logs]);

  const shown = days.slice(0, visibleDays);
  const canLoadMore = days.length > visibleDays || hasMoreOnServer;

  const handleShowMore = async () => {
    if (days.length > visibleDays) { setVisibleDays((n) => n + DAYS_PER_PAGE); return; }
    setIsFetchingMore(true);
    try {
      const { data, error } = await fetchFoodLogs(selectedClient.user_code, selectedClient.id, null, null, CHUNK_SIZE, logs.length);
      if (error) return;
      const rows = Array.isArray(data) ? data : (Array.isArray(data?.data) ? data.data : []);
      setHasMoreOnServer(rows.length >= CHUNK_SIZE);
      setLogs((prev) => {
        const seen = new Set(prev.map((l) => l.id));
        return [...prev, ...rows.filter((l) => !seen.has(l.id))];
      });
      setVisibleDays((n) => n + DAYS_PER_PAGE);
    } finally {
      setIsFetchingMore(false);
    }
  };

  const locale = isRTL ? 'he-IL' : 'en-US';
  const fmtTime = useCallback((iso) => (iso ? new Intl.DateTimeFormat(locale, { hour: 'numeric', minute: '2-digit' }).format(new Date(iso)) : ''), [locale]);
  const dayLabel = (day) => {
    const d = new Date(`${day}T00:00:00`);
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const diff = Math.round((today - d) / 864e5);
    const full = new Intl.DateTimeFormat(locale, { weekday: 'short', day: 'numeric', month: 'short' }).format(d);
    if (diff === 0) return { main: t('Today', 'היום'), sub: full };
    if (diff === 1) return { main: t('Yesterday', 'אתמול'), sub: full };
    return { main: full, sub: '' };
  };

  // One line over the visible days: how consistently they log, and what they eat on a logged day.
  const summary = useMemo(() => {
    if (!shown.length) return null;
    const total = sumTotals(shown.map((d) => d.totals));
    const n = shown.length;
    return { n, kcal: total.calories / n, protein: total.protein / n };
  }, [shown]);

  if (!selectedClient) {
    return (
      <ClientWorkspaceLayout>
        <div />
      </ClientWorkspaceLayout>
    );
  }

  const card = 'overflow-hidden rounded-lg border border-line bg-surface dark:border-line-dark dark:bg-surface-dark';

  return (
    <ClientWorkspaceLayout>
      <div className={`font-sans text-ink dark:text-ink-dark ${isDarkMode ? 'bg-canvas-dark' : 'bg-canvas'}`} dir={isRTL ? 'rtl' : 'ltr'}>
        <main className="mx-auto max-w-3xl px-4 py-6 sm:px-6 sm:py-8">
          <div className="mb-5">
            <h1 className="text-xl font-semibold tracking-tight">{t('Food logs', 'יומן ארוחות')}</h1>
            <p className="mt-0.5 text-sm text-ink-muted dark:text-ink-muted-dark">
              {status === 'ready' && summary
                ? t(
                  `What ${selectedClient.full_name?.split(' ')[0] || 'the client'} logged · last ${summary.n} ${summary.n === 1 ? 'day' : 'days'}: about ${fmtInt(summary.kcal)} kcal and ${round(summary.protein)} g protein a day`,
                  `מה ${selectedClient.full_name?.split(' ')[0] || 'הלקוח'} תיעד · ב-${summary.n} הימים האחרונים: בממוצע ${fmtInt(summary.kcal)} קק״ל ו-${round(summary.protein)} גרם חלבון ביום`,
                )
                : t('Every meal the client photographed or logged, day by day.', 'כל ארוחה שהלקוח צילם או תיעד, יום אחר יום.')}
            </p>
          </div>

          {status === 'loading' && (
            <div className="space-y-5" aria-busy="true">
              {[0, 1].map((d) => (
                <div key={d}>
                  <div className="mb-2 h-4 w-40 animate-pulse rounded bg-moss motion-reduce:animate-none dark:bg-moss-dark" />
                  <div className={card}>
                    {[0, 1, 2].map((r) => (
                      <div key={r} className="flex items-center gap-3 border-b border-line px-4 py-3 last:border-b-0 dark:border-line-dark">
                        <div className="h-14 w-14 animate-pulse rounded-lg bg-moss motion-reduce:animate-none dark:bg-moss-dark" />
                        <div className="flex-1 space-y-2">
                          <div className="h-3 w-32 animate-pulse rounded bg-moss motion-reduce:animate-none dark:bg-moss-dark" />
                          <div className="h-3 w-56 animate-pulse rounded bg-line motion-reduce:animate-none dark:bg-line-dark" />
                        </div>
                        <div className="h-4 w-14 animate-pulse rounded bg-moss motion-reduce:animate-none dark:bg-moss-dark" />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {status === 'error' && (
            <div role="alert" className={`${card} flex flex-col items-center px-6 py-12 text-center`}>
              <AlertTriangle size={28} className="mb-3 text-amber-700 dark:text-amber-400" aria-hidden="true" />
              <h2 className="text-base font-semibold">{t("Couldn't load the food logs", 'לא הצלחנו לטעון את יומן הארוחות')}</h2>
              <p className="mt-1 max-w-sm text-sm text-ink-muted dark:text-ink-muted-dark">{t('This is a loading problem, not an empty log. Try again in a moment.', 'זו תקלת טעינה ולא יומן ריק. נסו שוב בעוד רגע.')}</p>
              <button type="button" onClick={() => setReloadKey((k) => k + 1)} className="mt-4 inline-flex min-h-10 items-center gap-2 rounded-lg border border-line px-4 text-sm font-semibold hover:bg-moss dark:border-line-dark dark:hover:bg-moss-dark">
                <RefreshCw size={15} aria-hidden="true" /> {t('Try again', 'נסו שוב')}
              </button>
            </div>
          )}

          {status === 'ready' && days.length === 0 && (
            <div className={`${card} flex flex-col items-center px-6 py-14 text-center`}>
              <Camera size={32} className="mb-3 text-ink-muted opacity-60 dark:text-ink-muted-dark" aria-hidden="true" />
              <h2 className="text-base font-semibold">{t('No meals logged yet', 'עדיין לא תועדו ארוחות')}</h2>
              <p className="mt-1 max-w-sm text-sm text-ink-muted dark:text-ink-muted-dark">
                {t('Meals appear here as soon as the client photographs or logs them in the app.', 'ארוחות יופיעו כאן ברגע שהלקוח יצלם או יתעד אותן באפליקציה.')}
              </p>
            </div>
          )}

          {status === 'ready' && shown.length > 0 && (
            <div className="space-y-6">
              {shown.map(({ day, meals, totals }, dayIndex) => {
                const label = dayLabel(day);
                const fill = targetKcal > 0 ? Math.min(100, (totals.calories / targetKcal) * 100) : 0;
                return (
                  <section key={day} aria-labelledby={`day-${day}`} data-tour={dayIndex === 0 ? 'playbook-foodlogs-day' : undefined}>
                    <div className="mb-2 flex flex-wrap items-end justify-between gap-x-4 gap-y-1 px-1">
                      <h2 id={`day-${day}`} className="text-sm font-semibold">
                        {label.main}
                        {label.sub && <span className="ms-2 font-normal text-ink-muted dark:text-ink-muted-dark">{label.sub}</span>}
                      </h2>
                      <p className="text-[13px] tabular-nums text-ink-muted dark:text-ink-muted-dark">
                        <span className="font-semibold text-ink dark:text-ink-dark">{fmtInt(totals.calories)}</span>
                        {targetKcal > 0 && <> / {fmtInt(targetKcal)}</>} {t('kcal', 'קק״ל')}
                        <bdi dir="ltr" className="ms-3">P {round(totals.protein)} · C {round(totals.carbs)} · F {round(totals.fat)} g</bdi>
                      </p>
                    </div>
                    {targetKcal > 0 && (
                      <div className="mb-2 h-1 overflow-hidden rounded-full bg-moss dark:bg-moss-dark" aria-hidden="true">
                        <div className="h-full rounded-full bg-ink/50 dark:bg-ink-dark/50" style={{ width: `${fill}%` }} />
                      </div>
                    )}
                    <ul className={`${card} divide-y divide-line dark:divide-line-dark`}>
                      {meals.map((log) => (
                        <MealRow key={log.id} log={log} open={openId === log.id} onToggle={() => setOpenId((id) => (id === log.id ? null : log.id))} fmtTime={fmtTime} t={t} />
                      ))}
                    </ul>
                  </section>
                );
              })}

              {canLoadMore && (
                <div className="flex justify-center pt-2">
                  <button
                    type="button"
                    onClick={handleShowMore}
                    disabled={isFetchingMore}
                    className="inline-flex min-h-10 items-center gap-2 rounded-lg border border-line bg-surface px-5 text-sm font-semibold hover:bg-moss disabled:opacity-60 dark:border-line-dark dark:bg-surface-dark dark:hover:bg-moss-dark"
                  >
                    {isFetchingMore ? t('Loading…', 'טוען…') : t('Show earlier days', 'הצגת ימים קודמים')}
                  </button>
                </div>
              )}
            </div>
          )}
        </main>
      </div>
    </ClientWorkspaceLayout>
  );
}
